import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { cn } from '@/lib/utils';
import { Star } from 'lucide-react';
import { useState } from 'react';
import { usePostCleanerReviewMutation } from '@/hooks/mutations/use-post-cleaner-review.mutation';
import { usePostCleanerTipsMutation } from '@/hooks/mutations/use-post-cleaner-tips.mutation';
import { notifySuccess, notifyError } from '@/lib/notify';

// Define the form schema with zod (review text optional — matches customer app)
const reviewSchema = z.object({
  review: z.string().max(500, 'Review must be at most 500 characters'),
  rating: z
    .number()
    .min(1, 'Please select a rating')
    .max(5, 'Rating must be between 1 and 5'),
  tipAmount: z
    .number()
    .min(0, 'Tip amount must be positive')
    .optional()
    .nullable(),
});

// TypeScript type for the form data
type ReviewFormData = z.infer<typeof reviewSchema>;

const TIP_OPTIONS = [5, 10, 15, 20];

export function ReviewCleanerForm({
  cleanerId,
  bookingId,
  className,
  onSuccess,
  variant = 'default',
  ...props
}: React.ComponentProps<'div'> & {
  cleanerId: string;
  bookingId: string;
  onSuccess?: () => void;
  variant?: 'default' | 'dashboard';
}) {
  const [selectedTipOption, setSelectedTipOption] = useState<
    number | 'other' | null
  >(5);

  const { mutate: submitReview, isPending } = usePostCleanerReviewMutation();
  const { mutate: submitTip, isPending: isTipPending } =
    usePostCleanerTipsMutation();

  const form = useForm<ReviewFormData>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      review: '',
      rating: 5,
      tipAmount: 5,
    },
  });

  const onSubmit = async (data: ReviewFormData) => {
    try {
      submitReview(
        {
          feedback: (data.review ?? '').trim(),
          rating: data.rating,
          tips: data.tipAmount?.toString() || '0',
          booking_id: bookingId,
          cleaner_id: cleanerId,
        },
        {
          onSuccess: () => {
            notifySuccess('Your review has been submitted successfully!');

            // Submit tip if tip amount is greater than 0
            if (data.tipAmount && data.tipAmount > 0) {
              submitTip(
                {
                  cleaner_id: cleanerId,
                  booking_id: bookingId,
                  tip_amount_cents: Math.round(data.tipAmount * 100), // Convert dollars to cents
                },
                {
                  onSuccess: () => {
                    notifySuccess('Tip has been submitted successfully!');
                    // Reset form after successful submission
                    form.reset();
                    setSelectedTipOption(5);
                    onSuccess?.();
                  },
                  onError: (error: Error) => {
                    notifyError(
                      error.message || 'Failed to submit tip. Please try again.'
                    );
                    // Reset form even if tip fails
                    form.reset();
                    setSelectedTipOption(5);
                    onSuccess?.();
                  },
                }
              );
            } else {
              // Reset form after successful submission (no tip)
              form.reset();
              setSelectedTipOption(5);
              onSuccess?.();
            }
          },
          onError: (error: Error) => {
            notifyError(
              error.message || 'Failed to submit review. Please try again.'
            );
          },
        }
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Review submission error:', error);
    }
  };

  const isDashboard = variant === 'dashboard';

  const handleTipOptionSelect = (option: number | 'other') => {
    setSelectedTipOption(option);
    if (option !== 'other') {
      form.setValue('tipAmount', option);
    } else {
      form.setValue('tipAmount', 0);
    }
  };

  return (
    <div
      className={cn(
        isDashboard
          ? 'rounded-2xl bg-[#F5F5F5] p-5 sm:p-6 dark:bg-neutral-900 dark:ring-1 dark:ring-neutral-800'
          : 'rounded-lg bg-gray-50 p-4 dark:border dark:bg-neutral-800 sm:p-6',
        className
      )}
      {...props}
    >
      <h3
        className={cn(
          'mb-6 font-bold',
          isDashboard
            ? 'text-base text-[#333333] dark:text-white'
            : 'text-lg text-gray-900 dark:text-gray-100'
        )}
      >
        Write Review
      </h3>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Review Textarea */}
          <FormField
            control={form.control}
            name="review"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    placeholder="Write your review here (optional)"
                    className={cn(
                      'min-h-[150px] resize-none border border-gray-200 bg-white text-[#333333] placeholder:text-gray-400 dark:text-gray-100 dark:placeholder:text-gray-500',
                      isDashboard
                        ? 'rounded-xl dark:border-neutral-700 dark:bg-neutral-950'
                        : 'dark:bg-gray-800'
                    )}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Tips Section */}
          <FormField
            control={form.control}
            name="tipAmount"
            render={({ field }) => (
              <FormItem>
                <div className="space-y-3">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-3 sm:gap-4">
                    <FormLabel
                      className={cn(
                        'whitespace-nowrap text-sm font-medium',
                        isDashboard
                          ? 'text-[#333333] dark:text-gray-200'
                          : 'text-gray-700 dark:text-gray-300'
                      )}
                    >
                      Tips ($)
                    </FormLabel>
                    <div className="flex items-center gap-2 flex-wrap">
                      {TIP_OPTIONS.map(tip => (
                        <button
                          key={tip}
                          type="button"
                          onClick={() => handleTipOptionSelect(tip)}
                          className={cn(
                            'rounded-xl border-2 px-4 py-2.5 text-sm font-medium transition-colors sm:px-6 sm:py-2.5',
                            selectedTipOption === tip
                              ? isDashboard
                                ? 'border-[#ED7034] bg-[#ED7034]/15 text-[#333333] dark:text-white'
                                : 'border-orange-400 bg-orange-50 text-gray-900 dark:border-orange-500 dark:bg-orange-900/30 dark:text-orange-200'
                              : isDashboard
                                ? 'border-gray-200 bg-white text-[#333333] hover:border-gray-300 dark:border-neutral-700 dark:bg-neutral-950 dark:text-gray-200 dark:hover:border-neutral-600'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600'
                          )}
                        >
                          ${tip}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => handleTipOptionSelect('other')}
                        className={cn(
                          'rounded-xl border-2 px-4 py-2.5 text-sm font-medium transition-colors sm:px-6 sm:py-2.5',
                          selectedTipOption === 'other'
                            ? isDashboard
                              ? 'border-[#ED7034] bg-[#ED7034]/15 text-[#333333] dark:text-white'
                              : 'border-orange-400 bg-orange-50 text-gray-900 dark:border-orange-500 dark:bg-orange-900/30 dark:text-orange-200'
                            : isDashboard
                              ? 'border-gray-200 bg-white text-[#333333] hover:border-gray-300 dark:border-neutral-700 dark:bg-neutral-950 dark:text-gray-200 dark:hover:border-neutral-600'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600'
                        )}
                      >
                        Other
                      </button>
                    </div>
                    {selectedTipOption === 'other' && (
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="type you amount"
                          value={field.value || ''}
                          onChange={e =>
                            field.onChange(parseFloat(e.target.value) || 0)
                          }
                          className={cn(
                            'min-w-[150px] flex-1 rounded-xl border-2 sm:min-w-[200px]',
                            isDashboard
                              ? 'border-[#ED7034] bg-[#ED7034]/15 focus-visible:ring-[#ED7034]/40 dark:text-white'
                              : 'border-orange-400 bg-orange-50 dark:border-orange-500 dark:bg-orange-900/30 dark:text-orange-200 dark:focus-visible:ring-orange-500'
                          )}
                        />
                      </FormControl>
                    )}
                  </div>
                  <p
                    className={cn(
                      'text-sm',
                      isDashboard
                        ? 'text-[#666666] dark:text-gray-400'
                        : 'text-gray-600 dark:text-gray-400'
                    )}
                  >
                    If you happy with the cleaner, you can give them a tips
                  </p>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          {/* Rating Section */}
          <FormField
            control={form.control}
            name="rating"
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                  <FormControl>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => field.onChange(star)}
                          className="transition-transform hover:scale-110"
                        >
                          <Star
                            className={cn(
                              'h-6 w-6 sm:h-8 sm:w-8',
                              star <= field.value
                                ? isDashboard
                                  ? 'fill-[#FF6B00] text-[#FF6B00]'
                                  : 'fill-orange-500 text-orange-500'
                                : 'fill-gray-200 text-gray-200 dark:fill-neutral-700 dark:text-neutral-700'
                            )}
                          />
                        </button>
                      ))}
                    </div>
                  </FormControl>
                  <Button
                    type="submit"
                    size="lg"
                    loading={
                      isPending || isTipPending || form.formState.isSubmitting
                    }
                    className={cn(
                      'w-full font-bold sm:w-auto',
                      isDashboard
                        ? 'h-12 rounded-full bg-black px-8 text-base text-white hover:bg-neutral-900 dark:bg-white dark:text-black dark:hover:bg-gray-100'
                        : 'rounded-xl bg-black text-white hover:bg-gray-900 dark:bg-white dark:text-black dark:hover:bg-gray-100'
                    )}
                  >
                    {isPending || isTipPending
                      ? 'Submitting...'
                      : 'Submit Review'}
                  </Button>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </div>
  );
}
